import { ContainerBuilder } from '../../core/factories/container-builder.ts';
import { prescript } from '../../core/providers/prescript.ts';
import { UrlBuilder } from '../../core/helpers/url-builder.ts';
import { storeManager } from '../../core/store/store-manager.ts';
import { recommendationNotFoundEventHandler } from './events/recommendation-not-found.event.ts';
import { globalEventManager } from '../../core/events/event-manager.ts';
import type { IModule } from '../../core/interfaces/module.ts';
import { contentHeightEventHandler } from './events/content-height.event.ts';
import { resetPersonaHashEventHandler } from './events/reset-persona-hash.event.ts';

class Complementary implements IModule {
	private static readonly IFRAME_CONFIG = {
		id: 'fashion-hint-iframe-complementary',
		name: 'complementary',
		className: 'szb-complementary-container',
		type: 'iframe' as const,
		style: {
			width: '100%',
			border: 'none',
			height: '70vh',
		},
	};

    private removeScopedBridge?: () => void;

    async init(): Promise<void> {
		this.buildIframe();
		this.handleEvents();

        window.addEventListener('beforeunload', () => {
            this.removeScopedBridge?.();
        });
	}

	private buildIframe() {
		const iframeAlreadyExists = document.getElementById(Complementary.IFRAME_CONFIG.id);
		if (iframeAlreadyExists) {
			iframeAlreadyExists.remove();
		}

		const iframe = this.createIframe();
		const container = document.getElementById(prescript.complementaryContainerId);

		container?.appendChild(iframe);
	}

	private createIframe(): HTMLIFrameElement {
		const iframe = ContainerBuilder.execute({
			id: Complementary.IFRAME_CONFIG.id,
			name: Complementary.IFRAME_CONFIG.name,
			className: Complementary.IFRAME_CONFIG.className,
			type: Complementary.IFRAME_CONFIG.type,
		});

		Object.assign(iframe.style, Complementary.IFRAME_CONFIG.style);
		iframe.src = this.buildComplementaryUrl();

		return iframe;
	}

	private buildComplementaryUrl(): string {
		const sessionId = storeManager.getCookie('SIZEBAY_SESSION_ID_V4');
		const personaHash = storeManager.getCookie('SIZEBAY_PERSONA_HASH');

		return UrlBuilder.build(`${prescript.baseFashionHintUrl}complementary`, {
			baseUrl: prescript.baseVrfUrl,
			defaultUnit: prescript.defaultUnit,
			tenantId: prescript.tenantId,
			collectionName: prescript.collectionName,
			permalink: prescript.permalink,
			perPage: prescript.perPage,
			page: '1',
			sizeSystem: prescript.sizeSystem,
			similarityThreshold: prescript.similarityThreshold,
			currency: prescript.currency,
			lang: prescript.lang,
			productId: prescript.productId,
			sessionId,
			personaHash,
		});
	}

	private handleEvents() {
		const trustedOrigins = [new URL(prescript.baseFashionHintUrl).origin];
		globalEventManager.subscribePostMessages(trustedOrigins);

        this.removeScopedBridge = globalEventManager.subscribeCustomEvents(['szb:rebuild']);

        globalEventManager.subscribe<unknown>('szb:rebuild', () => {
            this.buildIframe();
        });

		globalEventManager.subscribe<{ height?: number; context?: 'complementary' }>(
			'postmessage:szb_content_height',
			payload => contentHeightEventHandler(payload)
		);

		globalEventManager.subscribe<{ type: string }>(
			'postmessage:szb_recommendation_not_found',
			payload => {
				recommendationNotFoundEventHandler(payload.type);
			}
		);

		globalEventManager.subscribe<unknown>('postmessage:szb_reset_persona_hash', async () => {
			await resetPersonaHashEventHandler();
			this.buildIframe();
		});

        window.addEventListener('szb:rebuild', () => {
            this.buildIframe();
        });
	}
}

export default new Complementary();
